/**
 * El recorte del código 2D del frente, SIN tapar, para que el server lo lea.
 *
 * El código prueba nombres, número, nacimiento y sexo, pero lleva adentro el
 * número de trámite, y por eso la foto que se entrega a SADAIC lo tiene
 * tapado. El server necesita leerlo: se le manda aparte, recortado de la foto
 * original, en el mismo envío; el server lo lee y lo descarta. Acá no se
 * guarda: se arma en el momento de enviar.
 */
import { canvasToJpegBase64, RedactRect } from './imagePrep';

/** La caja que cubre todas las zonas, con un margen proporcional y dentro de la foto. */
export function cajaDelCodigo(
  zonas: readonly RedactRect[],
  ancho: number,
  alto: number,
  margen = 0.08
): RedactRect | null {
  const validas = zonas.filter((z) => z.w > 0 && z.h > 0);
  if (validas.length === 0 || ancho <= 0 || alto <= 0) return null;
  const x0 = Math.min(...validas.map((z) => z.x));
  const y0 = Math.min(...validas.map((z) => z.y));
  const x1 = Math.max(...validas.map((z) => z.x + z.w));
  const y1 = Math.max(...validas.map((z) => z.y + z.h));
  const mx = (x1 - x0) * margen;
  const my = (y1 - y0) * margen;
  const x = Math.max(0, Math.floor(x0 - mx));
  const y = Math.max(0, Math.floor(y0 - my));
  const xf = Math.min(ancho, Math.ceil(x1 + mx));
  const yf = Math.min(alto, Math.ceil(y1 + my));
  if (xf <= x || yf <= y) return null;
  return { x, y, w: xf - x, h: yf - y };
}

/** El JPEG (base64, sin prefijo) del recorte, o null si no hay qué recortar. */
export function recortarCodigo(origen: HTMLCanvasElement, zonas: readonly RedactRect[]): string | null {
  const caja = cajaDelCodigo(zonas, origen.width, origen.height);
  if (!caja) return null;
  const recorte = document.createElement('canvas');
  recorte.width = caja.w;
  recorte.height = caja.h;
  try {
    const ctx = recorte.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(origen, caja.x, caja.y, caja.w, caja.h, 0, 0, caja.w, caja.h);
    return canvasToJpegBase64(recorte);
  } finally {
    // El lienzo temporal se suelta enseguida, ya codificado o no: Safari en iOS
    // tiene un tope de memoria para los canvas vivos.
    recorte.width = 0;
    recorte.height = 0;
  }
}
